import styled from 'styled-components';
import { useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import { Typography } from 'design-system/src/components/Typography/Typography';
import ButtonRow, { Alignment } from 'design-system/src/components/ButtonRow/ButtonRow';
import Button from 'design-system/src/components/Button/Button';
import { InfoBox } from 'design-system/src/components/InfoBox/InfoBox';
import Hairline from 'design-system/src/components/Hairline/Hairline';
import { LoadingSpinner } from 'design-system/src/components/LoadingSpinner/LoadingSpinner';
import { PageLoadError } from 'design-system/src/components/PageLoadError/PageLoadError';
import { StandardErrorToast } from 'design-system/src/components/Toast/Toast';
import PageContainer from '../components/PageContainer';
import { ExtraInfoTable } from '../components/ExtraInfoTable/ExtraInfoTable';
import { client } from '../api';
import { EnrollmentType } from '../types';
import { ItemViewTopNav } from '../components/tableItemDetailViewComponents/ItemViewTopNav';
import {
  BodyContainer,
  TopNavAndContent,
  ContentOuterWrapper,
  CenteredContent,
} from '../components/tableItemDetailViewComponents/Containers';
import { formatStandardDate } from '../utils';
import SignFormModal from '../components/enrollments/SignFormModal/SignFormModal';
import InitiationSuccessModal from '../components/enrollments/InitiationSuccessModal/InitiationSuccessModal';
import { FormActionContainer, StickyButtonContainer, StyledIframe } from '../components/tasks/TaskActions';
import { toast } from 'react-hot-toast';

const SpacedContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const StyledInfoBox = styled(InfoBox)`
  flex-direction: column;
`;

export type PendingEnrollment = {
  drug_name: string;
  enrollment_type: keyof typeof EnrollmentType;
  template_name: string;
  embedded_form_url: string;
  patient_first_name: string;
  patient_last_name: string;
  dob: string;
  status: 'not_started' | 'patient_consent_received' | 'matched';
  prescription_id: string;
  requires_signature: boolean;
};

/**
 * Page for creating a pending enrollment created by EnrollmentModal
 */
export const PendingEnrollmentPage = () => {
  const [showSignFormModal, setShowSignFormModal] = useState(false);
  const [showInitiationSuccessModal, setShowInitiationSuccessModal] = useState(false);
  const queryClient = useQueryClient();
  const isTrainingMode = import.meta.env.VITE_TRAINING_MODE === 'true';

  const { enrollmentId } = useParams();
  const pendingEnrollmentQuery = useQuery<PendingEnrollment>({
    queryKey: ['pendingEnrollment', enrollmentId],
    queryFn: async () => {
      const res = await client.get(`enrollments/pending/${enrollmentId}`);
      return res.data;
    },
  });

  const initiateEnrollmentMutation = useMutation({
    mutationFn: async () => {
      if (isTrainingMode) {
        console.warn('Training Mode: Mocking enrollment initiation, no real request sent.');

        // Simulating a successful response without sending the request
        return Promise.resolve({ success: true });
      }

      await client.post(`enrollments/${enrollmentId}/initiate`);
    },
    onSuccess: () => {
      setShowInitiationSuccessModal(true);
      queryClient.invalidateQueries({
        queryKey: ['pendingEnrollment', enrollmentId],
      });
    },
    onError: () => {
      toast.custom(() => <StandardErrorToast />);
    },
  });

  let body;
  if (pendingEnrollmentQuery.isLoading) {
    body = <LoadingSpinner />;
  } else if (pendingEnrollmentQuery.isError || !pendingEnrollmentQuery.data) {
    body = <PageLoadError />;
  } else {
    const {
      drug_name,
      enrollment_type,
      template_name,
      embedded_form_url,
      patient_first_name,
      patient_last_name,
      dob,
      status,
      prescription_id,
      requires_signature,
    } = pendingEnrollmentQuery.data;

    const notStartedStep = (
      <>
        <SpacedContent>
          <Typography styledAs="bodyLargeSpaceGrotesk" renderedAs="h2" weight={500}>
            Submission instructions
          </Typography>

          <Typography styledAs="bodySmallDMSans">
            A pre-filled enrollment form has been generated with the patient information provided.
          </Typography>
          <Typography styledAs="bodySmallDMSans">
            Upon receiving the incoming prescription for this enrollment, we will complete any remaining sections based
            on the details in the matching prescription.
          </Typography>
          <Typography styledAs="bodySmallDMSans">
            Please review the form below and collect the patient's signature. Complete any remaining sections to your
            liking but please know that it's <strong>optional</strong> as Tandem will autofill the remaining sections
            upon matching the prescription.
          </Typography>
        </SpacedContent>

        <Hairline />

        <StyledInfoBox includeIcon={false} variant="surface">
          <Typography weight={600}>
            {requires_signature
              ? 'Please review the form to ensure your details are correct. Provide a signature from the patient using the button below to initiate patient enrollment.'
              : 'Please review the form to ensure your details are correct. Use the button below to initiate patient enrollment.'}
          </Typography>
        </StyledInfoBox>

        <FormActionContainer>
          <StyledIframe src={embedded_form_url} />

          <StickyButtonContainer>
            <ButtonRow align={Alignment.Center}>
              <Button
                size="sm"
                width="full"
                onClick={() => {
                  if (requires_signature) {
                    setShowSignFormModal(true);
                  } else {
                    initiateEnrollmentMutation.mutate();
                  }
                }}
                isLoading={initiateEnrollmentMutation.isPending}
              >
                {requires_signature ? 'Collect patient e-signature' : 'Initiate enrollment form'}
              </Button>

              <Typography styledAs="bodyExtraSmallDMSans" color="var(--dark-gray)">
                {requires_signature ? 'After signing' : 'After initiating enrollment'}, this form will become{' '}
                <strong>uneditable</strong> until matched with its prescription.
              </Typography>
            </ButtonRow>
          </StickyButtonContainer>
        </FormActionContainer>
      </>
    );

    const patientConsentReceivedStep = (
      <>
        <SpacedContent>
          <Typography styledAs="bodyLargeSpaceGrotesk" renderedAs="h2" weight={500}>
            This enrollment form is currently pending.
          </Typography>

          <Typography styledAs="bodySmallDMSans">
            We're waiting for the matching prescription to arrive, which usually occurs within 24 hours of it being
            sent. Once matched, you'll be redirected to the prescription page to sign the completed form and manage the
            prescription.
          </Typography>
          <Typography styledAs="bodySmallDMSans">
            Upon receiving the incoming prescription for this enrollment, we will complete any remaining sections based
            on the details in the matching prescription.
          </Typography>
          <Typography styledAs="bodySmallDMSans">No action is needed at this time.</Typography>
        </SpacedContent>
      </>
    );

    const getBodyContent = () => {
      if (status === 'not_started') {
        return notStartedStep;
      } else if (status === 'patient_consent_received') {
        return patientConsentReceivedStep;
      } else if (status === 'matched' && prescription_id) {
        return <Navigate to={`/rxs/${prescription_id}`} replace />;
      } else {
        return <PageLoadError />;
      }
    };

    body = (
      <BodyContainer>
        <TopNavAndContent>
          <ItemViewTopNav noSidebar />

          <ContentOuterWrapper>
            <CenteredContent>
              <Typography styledAs="h6" marginBottom="1rem">
                New enrollment form
              </Typography>

              <ExtraInfoTable
                data={[
                  {
                    label: 'Drug',
                    value: drug_name,
                  },
                  {
                    label: 'Patient',
                    value: `${patient_first_name} ${patient_last_name} (${formatStandardDate(dob)})`,
                  },
                  {
                    label: 'Enrollment type',
                    value: EnrollmentType[enrollment_type],
                  },
                  {
                    label: 'Form name',
                    value: template_name,
                  },
                ]}
                minFieldLabelWidth="140px"
              />

              {getBodyContent()}

              {showSignFormModal && <SignFormModal onClose={() => setShowSignFormModal(false)} />}
              {showInitiationSuccessModal && (
                <InitiationSuccessModal onClose={() => setShowInitiationSuccessModal(false)} />
              )}
            </CenteredContent>
          </ContentOuterWrapper>
        </TopNavAndContent>
      </BodyContainer>
    );
  }

  return <PageContainer noPadding={true}>{body}</PageContainer>;
};

export default PendingEnrollmentPage;
